import gql from 'graphql-tag';
import { kmsClient } from 'api';
import { useLazyQuery } from '@apollo/react-hooks';
import { CAMPAIGN_SETTINGS_PUBLISHERS_FRAGMENT } from '../fragments/campaignSettingsPublishers.fragment';
import {
  FacebookCampaignSettingsQuery,
  FacebookCampaignSettingsQueryVariables
} from './__generated__/FacebookCampaignSettingsQuery';

export const FACEBOOK_CAMPAIGN_SETTINGS_QUERY = gql`
  query FacebookCampaignSettingsQuery($id: ID!) {
    campaign(id: $id) {
      #-----------------------------------------------------------
      # Root Attributes
      #-----------------------------------------------------------
      adsForCampaign
      publisher {
        ...PublisherFragment
      }
      defaultFacebookAccount {
        id
        name
        accountId
      }

      #-----------------------------------------------------------
      # General Attributes
      #-----------------------------------------------------------
      id
      name
      start
      end
      budget
      remainingBudget
      useCase
      subUseCase
      dsaBeneficiary
      dsaPayor
      defaultFacebookAccountId
      autoTargetingEnabled

      #-----------------------------------------------------------
      # Billing Attributes
      #-----------------------------------------------------------
      fee
      tier
      billingCenter

      #-----------------------------------------------------------
      # URL & Domains Settings Attributes
      #-----------------------------------------------------------
      whitelistDomains
      defaultAdConversionDomain
      defaultLeadgenTrackingParams
      utmParameters
      adMacroTags
      whitelistedUrlParams
      urlSegment
      allowUrlRedirects
      allowUtmParamWithoutValue

      #-----------------------------------------------------------
      # Technical Attributes
      #-----------------------------------------------------------
      campaignFacebookPages {
        facebookPageId
        facebookPageDisplayName
        newPageExperience
        instagramAccountId
        instagramAccountType
      }
      dynamicAudienceUpload
      targetingExpansionEnabled
      shouldDeleteOldAds
      automaticWcaCreationEnabled
      dynamicWcaExclusionEnabled
      tagBrandPage
      productCatalogId
      reachFrequencyCapResetPeriodDays
      unifiedConversionPixel
      unifiedTrackingPixels
      snowplowAppIds
      facebookAppId
      appStoreUrl

      #-----------------------------------------------------------
      # Optimization Settings Attributes
      #-----------------------------------------------------------
      defaultObjective
      optimizationGoal
      defaultArbitrageOptimizationStrategy
      appOptimizationEvent
      defaultArbitragePageViewsPerSessionGoal

      #-----------------------------------------------------------
      # Budget Optimization Strategy Attributes
      #-----------------------------------------------------------
      budgetOptimizationStrategy
      runnerProfile
      joggerProfile
      goalCpa
      cpaRiskFactor
      maxCpa
      dailyMonitorBudget
      defaultArticleDailyBudget
      runnerDailyBreachMargin
      runnerLimitMaxBudget
      runnerMonitorArticleCapChange
      runnerResetBudgetOnRecovery
      autoBudgetSpread
      autoBidChange
      scheduledDeliveryEnabled
      scheduledDeliveryStartTime
      scheduledDeliveryEndTime

      #-----------------------------------------------------------
      # Default Values Attributes
      #-----------------------------------------------------------
      defaultDailyBudget
      bidStrategy
      defaultActionsBid

      # ---------- Placement Attributes ---------- #
      automaticAdPlacementEnabled
      defaultPlacementPublisherPlatforms
      defaultPlacementDevicePlatforms
      defaultPlacementFacebookPositions
      defaultPlacementInstagramPositions
      defaultPlacementMessengerPositions
      defaultUserOs
      defaultUserDevice
      defaultCustomAudiences
      excludedCustomAudiences

      # ---------- Targeting Attributes ---------- #
      savedAudienceId
      useSavedAudience
      defaultAgeMin
      defaultAgeMax
      defaultGender
      language
      defaultLocalesFromLanguage
      defaultCountries
      defaultRegions
      defaultMediumGeoAreas
      defaultCities
      defaultZipCodes
      defaultBehaviors
      defaultRelationshipStatuses

      #-----------------------------------------------------------
      # Keywee Story Attributes
      #-----------------------------------------------------------
      storyAudienceCampaignSetting {
        id
        campaignEnabled
        wcaLookalikeEnabled
        fresherWcaLookalikeEnabled
        defaultTargetingEnabled
        lookalikeAudienceSize
        lastUploadTime
      }
      audienceAdvantageEnabled

      #-----------------------------------------------------------
      # UI Settings Attributes
      #-----------------------------------------------------------
      # Dynamic arguments (column_name=configuration)
      # [facebookConversionFields, dailyGraphFields, keyweeConversionFields, externalConversionFields, reportBreakdownOptions, attributionWindow, dailyConversionGraph]
      configuration {
        facebookConversionFields
        dailyGraphFields
        keyweeConversionFields
        externalConversionFields
        reportBreakdownOptions
        attributionWindow
        dailyConversionGraph
      }

      clicksReportSource
      externalArchived
      kssKeyweeCreativesLiftMetricEnabled
      kssKeyweeCreativesLiftMetricConversionId
      goal
      defaultGoalAttributionWindow
      targetGoalType
      targetGoalValue
      goalDescription
      targetDailySpend

      #-----------------------------------------------------------
      # Revenue Import Settings Attributes
      #-----------------------------------------------------------
      isHugoOn
      clientFeedbackResolution
      clientFeedbackKind
      goalCppvDollars
      clientFeedbackSource
      goalProfitMargin
      goalCpcTopDollars
      goalCpcBottomDollars
      hugoImportBufferEnabled
      hugoImportBuffer

      #-----------------------------------------------------------
      # Data Sources Attributes
      #-----------------------------------------------------------
      contentDataSources {
        id
        dataSourceType
        name
        path
      }

      #-----------------------------------------------------------
      # Flow Settings Attributes
      #-----------------------------------------------------------
      creativeApprovalIsEnabled

      #-----------------------------------------------------------
      # Page-Locked Articles Attributes
      #-----------------------------------------------------------
      selfService
      pageLockedArticlesEnabled
      unassignedArticlesCount
      facebookPageAssignments {
        facebookPageId
        facebookPageDisplayName
        assignedArticlesCount
        trackingParamKey
        trackingParamValue
      }

      #-----------------------------------------------------------
      # URL Validation Bypass
      #-----------------------------------------------------------
      urlValidationBypass

      #-----------------------------------------------------------
      # Article Expiration Settings Attributes
      #-----------------------------------------------------------
      articleExpirationSettings {
        id
        enabled
        ruleType
        defaultDuration
        expirationRules {
          id
          duration
          utmKey
          utmValue
        }
      }

      #-----------------------------------------------------------
      # Organic Upload Settings Attributes
      #-----------------------------------------------------------
      organicUploadSettings {
        id
        enabled
        type
        maxArticlesToUpload
        enableCreativeApproval
        enableCreativeSuggestions
        maxCreativeSuggestions
        excludeTaggedSponsoredPosts
        enablePoliticalContent
        politicalContentAction
        enablePhotoPosts
        enableLeadgenFormCreation
        enableLeadgenFormNamePrefix
        enableLeadgenFormNameSuffix
        leadgenFormExternalId
        creativeCallToAction
        leadgenFormNamePrefix
        leadgenFormNameSuffix
        whitelistEnabled
        blacklistEnabled
        whitelistedWords
        blacklistedWords
        rssSettings {
          startDate
          url
        }
      }

      #-----------------------------------------------------------
      # Organic Upload Settings Attributes
      #-----------------------------------------------------------
      discountSettings {
        id
        enabled
        discountType
        discountValue
      }
    }
  }
  ${CAMPAIGN_SETTINGS_PUBLISHERS_FRAGMENT}
`;

export const useFacebookCampaignSettingsLazyQuery = () => {
  return useLazyQuery<FacebookCampaignSettingsQuery, FacebookCampaignSettingsQueryVariables>(
    FACEBOOK_CAMPAIGN_SETTINGS_QUERY,
    { client: kmsClient }
  );
};
